import type { GadsFieldsRequired } from '..'
import type { common, enums, resources } from '../gads'

export interface Keyword {
    text: string
    rawText: string
    adGroupResourceName: string
    resourceName: string
    metrics: Metrics
}

export interface AdScore {
    total: number
    convertingSearchTerms: number
    averageCtr: number
    updatedRecently: number
    minimumHeadlines: number
    noAds?: boolean
}

export interface Metrics {
    impressions: number
    cost: number
    clicks: number
    conversions: number
    conversionsValue: number
}

export enum RsaStatus {
    PAUSED,
    ENABLED,
}

export enum RsaStrength {
    UNSPECIFIED,
    UNKNOWN,
    PENDING,
    NO_ADS,
    POOR,
    AVERAGE,
    GOOD,
    EXCELLENT,
}

export enum AssetStatPerformanceLabel {
    UNRATED = 'unrated',
    LEARNING = 'learning',
    LOW = 'low',
    GOOD = 'good',
    BEST = 'best',
}

export interface EditableRsaAdFields {
    finalUrl: string
    finalUrlMobile: string
    finalUrlSuffix: string
    trackingUrlTemplate: string
    pathOne: string
    pathTwo: string
    headlines: RsaAsset[]
    descriptions: RsaAsset[]
}

export interface GadsRsa {
    ad_group_ad: GadsFieldsRequired<
        resources.AdGroupAd,
        'resource_name' | 'ad_strength' | 'status' | 'ad' | 'status'
    >
    ad_group: GadsFieldsRequired<resources.AdGroup, 'resource_name' | 'id' | 'name'>
    campaign: GadsFieldsRequired<resources.Campaign, 'resource_name' | 'id' | 'name'>
    metrics: GadsFieldsRequired<
        common.Metrics,
        'all_conversions' | 'clicks' | 'all_conversions_value' | 'impressions' | 'cost_micros'
    >
}

export type GadsEta = GadsRsa // yep

export interface RsaAd extends EditableRsaAdFields {
    urlBase: string
    adGroupResourceName: string
    campaignResourceName: string
    resourceName: string
    metrics: Metrics
    adStrength: RsaStrength
    adScore: AdScore
    status: RsaStatus
    draftId: string
}

export interface EtaAd extends EditableRsaAdFields {
    urlBase: string
    adGroupResourceName: string
    campaignResourceName: string
    resourceName: string
    metrics: Metrics
    status: RsaStatus
}

export interface RsaSelectionRow {
    resourceName: string
    name: string
    metrics: Metrics
    adScore: AdScore
}

interface RsaSelectionAdGroup extends RsaSelectionRow {
    rsas: RsaAd[]
    etas: EtaAd[]
    keywords: Keyword[]
}

interface RsaSelectionCampaign extends RsaSelectionRow {
    adGroups: RsaSelectionAdGroup[]
}

export type RsaAssetType = 'headlines' | 'descriptions'

export interface RsaAsset {
    text: string
    pinnedTo: number // 0 is not pinned
}

export type AssetPerformanceLabelString = keyof typeof enums.AssetPerformanceLabel

type ScoreTooltip = Record<AssetStatPerformanceLabel, number>

export type RsaAssetStat = {
    text: string
    score: number
    scoreTooltip: Partial<ScoreTooltip>
}

interface RsaAssetStats {
    accountLevel: { [k in RsaAssetType]: RsaAssetStat[] }
    campaignLevel: { [resource_name: string]: { [k in RsaAssetType]: RsaAssetStat[] } }
}

export interface AccountStatValues extends Metrics {
    readableType: string
    count: number
}

export interface AccountStats {
    adGroupLevel: Record<string, { [k: string]: AccountStatValues }>
    rsaLevel: Record<string, { [k: string]: AccountStatValues }>
}

export interface RsaSelectionData {
    campaigns: RsaSelectionCampaign[]
    drafts: Draft[]
    rsaAssetStats: RsaAssetStats
    accountStats: AccountStats
    urlBase: string
    searchTermsPerCampaign: SearchTermsDictionary
    topSearchTermsPerAdGroup: SearchTermsDictionary
}

export interface SearchTerm {
    id: string
    text: string
    metrics: Metrics
}

export type SearchTermsDictionary = Record<string, SearchTerm[]>

export interface OTableSearchTermRow extends SearchTerm {
    includedInRsa: boolean
}

export type RsaTextFields = Pick<
    RsaAd,
    'urlBase' | 'pathOne' | 'pathTwo' | 'headlines' | 'descriptions'
>

export type CompetitorAd = RsaTextFields

export interface TextIdea {
    text: string
    score: number
}

export interface WritesonicAssets {
    headlines: TextIdea[]
    descriptions: TextIdea[]
}

export interface Draft {
    id: string
    adGroupId: number
    baseAdId?: number
    draftFields: EditableRsaAdFields
}
